import {
  getLeoUuid,
  LeoUUID,
  getList,
  getBoolean,
  getUrl,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { TableFilter, TableFilterDTO, TableFilterEnums } from "../types/tableFilter";
/* eslint-disable import/export */
export abstract class GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC implements
  LeoRPC<GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC.Request, GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC.Response, GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC.Errors.Errors>
{
  abstract execute(
    request: GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC.Request
  ): Promise<LeoRPCResult<GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC.Response, GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC.Errors.Errors>>;
}

export namespace GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    filters: TableFilterDTO[];
    isLive: boolean;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly filters: TableFilter[];
    readonly isLive: boolean;
  
    static readonly constraints = {
      "filters": {
        "maxSize": 5,
      },
    }
  
    constructor(
    projectId: LeoUUID, 
    filters: TableFilter[], 
    isLive: boolean
    ) {
      this.projectId = projectId;
      Request.validateFilters(filters);
      this.filters = filters;
      this.isLive = isLive;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const filters = getList<TableFilterDTO, TableFilter>(
        dto,
        "filters",
        TableFilter.fromDTO,
        InvalidRequestFiltersError
      );
      const isLive = getBoolean(dto, "isLive", InvalidRequestIsLiveError);
      return new Request(
        projectId,
        filters,
        isLive
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          filters: this.filters.map((e: TableFilter) => { return e.toDTO() }),
          isLive: this.isLive
      };
      return dto;
    }
    private static validateFilters = (filters: TableFilter[]): void => {
      if(filters.length > 5) {
        throw new InvalidRequestFiltersMaxSizeError(
          5,
          `Attribute filters is too long. Size is ${filters.length}. Max size is 5.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC(projectId='${this.projectId}',filters='${this.filters}',isLive='${this.isLive}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    documentURL: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleResponse: boolean = true;
  
    readonly documentURL: URL;
  
  
    constructor(
    documentURL: URL
    ) {
      this.documentURL = documentURL;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const documentURL = getUrl(dto, "documentURL", InvalidResponseDocumentUrlError);
      return new Response(
        documentURL
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          documentURL: this.documentURL.toString()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModule(documentURL='${this.documentURL}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    DeletedColumnFilter = "DELETED_COLUMN_FILTER"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface DeletedColumnFilterDTO {
      code: string;
    }
    
    export class DeletedColumnFilter extends LeoRPCError<RPCError> {
      readonly isDeletedColumnFilter: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DeletedColumnFilter;
        super(code);
      }
    
    
      toDTO(): DeletedColumnFilterDTO {
        const dto: DeletedColumnFilterDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DeletedColumnFilter.prototype.toString = function toString(): string {
      return `GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC.DELETED_COLUMN_FILTER()`;
    };
    

    export type Errors = InvalidProjectId | DeletedColumnFilter;
    export type ErrorsDTO = InvalidProjectIdDTO | DeletedColumnFilterDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.DeletedColumnFilter:
          return new DeletedColumnFilter();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestFiltersError extends GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFiltersError";
  }
}

export class InvalidRequestFiltersMaxSizeError extends InvalidRequestFiltersError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFiltersMaxSizeError";
  }
}

export class InvalidRequestIsLiveError extends GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestIsLiveError";
  }
}

export class InvalidResponseDocumentUrlError extends GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDocumentUrlError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseDeletedColumnFilterError extends GetPresignedDownloadURLForRespondentsWithFiltersForRespondentModuleRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDeletedColumnFilterError";
  }
}

